// Motion Director - The Conductor of All Movement
// Defines the rhythm, timing, and orchestration of animations site-wide

// TIMING CONSTANTS - The Foundation
export const TIMING = {
  // Breath - Intentional pauses
  breath: {
    short: 0.15,    // Between quick actions
    medium: 0.35,   // After major reveals
    long: 0.6,      // End of sequences
  },
  
  // Duration - How long things move
  duration: {
    instant: 0.2,   // Micro-interactions
    quick: 0.5,     // Small elements
    normal: 0.8,    // Standard content
    slow: 1.2,      // Large, important content
    cinematic: 1.6, // Hero moments
  },
  
  // Stagger - Sequential reveals
  stagger: {
    tight: 0.08,    // Subtle coordination
    normal: 0.12,   // Clear sequence
    relaxed: 0.2,   // Emphasized sequence
    dramatic: 0.35, // High-value content
  }
};

// EASING CURVES - The Feel
export const EASING = {
  // Smooth, professional motion
  cinematic: [0.22, 1, 0.36, 1],
  
  // Quick, responsive actions
  snappy: [0.34, 1.56, 0.64, 1],
  
  // Gentle, calm motion
  gentle: [0.25, 0.46, 0.45, 0.94],
  
  // Simple fade
  fade: "easeOut",
};

// SECTION ORCHESTRATION - The Conductor's Score
export const SECTIONS = {
  hero: {
    breath: TIMING.breath.medium,
    sequences: {
      background: {
        start: TIMING.breath.medium,
        duration: TIMING.duration.slow,
        easing: EASING.cinematic,
      },
      headline: {
        start: TIMING.breath.medium + TIMING.duration.slow * 0.7,
        duration: TIMING.duration.normal,
        stagger: TIMING.stagger.tight,
        easing: EASING.cinematic,
      },
      cta: {
        start: TIMING.breath.medium + TIMING.duration.slow * 0.7 + TIMING.duration.normal * 0.6,
        duration: TIMING.duration.normal,
        stagger: TIMING.stagger.normal,
        easing: EASING.snappy,
      },
    },
    pause: TIMING.breath.long, // Intentional pause at the end
  },
  
  about: {
    type: 'highValue',
    stagger: TIMING.stagger.dramatic,
    duration: TIMING.duration.slow,
    direction: 'left',
  },
  
  skills: {
    type: 'static', // Intentional rest for user attention
    duration: TIMING.duration.quick,
  },
  
  experience: {
    type: 'highValue',
    stagger: TIMING.stagger.dramatic,
    duration: TIMING.duration.slow,
    direction: 'right',
  },
  
  projects: {
    type: 'highValue',
    stagger: TIMING.stagger.relaxed,
    duration: TIMING.duration.normal,
    direction: 'bottom',
  },
  
  certifications: {
    type: 'supporting',
    stagger: TIMING.stagger.tight,
    duration: TIMING.duration.quick,
    direction: 'left',
  },
  
  contact: {
    type: 'cta',
    stagger: TIMING.stagger.normal,
    duration: TIMING.duration.normal,
    direction: 'right',
  },
};

// MOTION VARIANTS - Reusable Patterns
export const createSectionVariants = (sectionConfig) => {
  const { type, stagger, duration, direction = 'bottom' } = sectionConfig;
  
  // Direction mapping
  const directionMap = {
    left: { x: -50, y: 0 },
    right: { x: 50, y: 0 },
    bottom: { x: 0, y: 40 },
    top: { x: 0, y: -40 },
  };
  
  const offset = directionMap[direction] || directionMap.bottom;
  
  // Static sections (no motion)
  if (type === 'static') {
    return {
      container: {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { duration: duration || TIMING.duration.quick, ease: EASING.fade }
        }
      },
      item: {
        hidden: { opacity: 0 },
        visible: {
          opacity: 1,
          transition: { duration: duration || TIMING.duration.quick, ease: EASING.fade }
        }
      }
    };
  }
  
  // Animated sections
  return {
    container: {
      hidden: {},
      visible: {
        transition: {
          staggerChildren: stagger || TIMING.stagger.normal,
        }
      }
    },
    item: {
      hidden: { opacity: 0, ...offset },
      visible: {
        opacity: 1,
        x: 0,
        y: 0,
        transition: { 
          duration: duration || TIMING.duration.normal, 
          ease: EASING.cinematic 
        }
      }
    }
  };
};

// INTERACTION FEEDBACK - Unified Physical Response
export const INTERACTIONS = {
  // Card hover - Depth and shadow
  card: {
    rest: {},
    hover: {
      boxShadow: '0 20px 40px -15px rgba(139, 0, 0, 0.5), 0 0 0 2px rgba(251, 191, 36, 0.1)',
      borderColor: 'rgba(185, 28, 28, 0.6)',
      transition: { duration: TIMING.duration.instant, ease: EASING.gentle }
    }
  },
  
  // Card content - Micro-shift
  cardContent: {
    rest: {},
    hover: {
      x: 3,
      y: -2,
      transition: { duration: TIMING.duration.instant, ease: EASING.gentle }
    }
  },
  
  // Button - Physical press
  button: {
    rest: {},
    hover: {
      boxShadow: '0 10px 30px -10px rgba(217, 119, 6, 0.5), 0 0 0 1px rgba(251, 191, 36, 0.2)',
      borderColor: 'rgba(217, 119, 6, 0.8)',
      transition: { duration: TIMING.duration.instant, ease: EASING.gentle }
    },
    tap: {
      scale: 0.95,
      transition: { duration: 0.1, ease: EASING.gentle }
    }
  },
  
  // Icon - Subtle rotation
  icon: {
    rest: {},
    hover: {
      rotate: 5,
      scale: 1.1,
      transition: { duration: TIMING.duration.instant, ease: EASING.snappy }
    }
  }
};

export default {
  TIMING,
  EASING,
  SECTIONS,
  createSectionVariants,
  INTERACTIONS,
};
